// Google Analytics 4 Event Tracking
// Uses the gtag that's already loaded in index.html

declare global {
  interface Window {
    gtag: (
      command: string,
      action: string,
      params?: Record<string, any>
    ) => void;
  }
}

// Helper to safely call gtag
const gtag = (...args: Parameters<Window["gtag"]>) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag(...args);
  }
};

// Track page view - needed for Wallet Connect Rate calculation
// Rate = wallet_connect events / page_view events
export const trackPageView = (pageName: string = "home") => {
  gtag("event", "page_view", {
    page_title: pageName,
    page_location: window.location.href,
  });
};

// Track when a user connects their wallet
// For "Wallet Connect Rate": compare this to page_view in GA4
export const trackWalletConnect = (walletType: string = "unknown") => {
  gtag("event", "wallet_connect", {
    event_category: "engagement",
    event_label: walletType,
  });
};

// Track when a user disconnects their wallet
export const trackWalletDisconnect = () => {
  gtag("event", "wallet_disconnect", {
    event_category: "engagement",
  });
};

// Track when a bet is placed
export const trackBetPlaced = (
  amount: number,
  token: string,
  country: string,
  chainId: number
) => {
  gtag("event", "bet_placed", {
    event_category: "transaction",
    value: amount,
    currency: "USD", // All bets settle to USDT
    token: token,
    country: country,
    chain_id: chainId,
  });
};

// Track bet success (after confirmation)
// This is the key event for "Cost per Bet" metric
export const trackBetSuccess = (
  amount: number,
  token: string,
  txHash: string
) => {
  // Standard event for bet tracking
  gtag("event", "bet_success", {
    event_category: "transaction",
    value: amount,
    currency: "USD",
    token: token,
    transaction_id: txHash,
  });

  // GA4 purchase event for revenue tracking (Cost per Bet)
  gtag("event", "purchase", {
    transaction_id: txHash,
    value: amount,
    currency: "USD",
    items: [
      {
        item_id: "bet",
        item_name: "World Cup Bet",
        price: amount,
        quantity: 1,
      },
    ],
  });
};

// Track bet failure
export const trackBetFailed = (error: string) => {
  gtag("event", "bet_failed", {
    event_category: "transaction",
    error_message: error.substring(0, 100), // Limit error length
  });
};

// Track pool value on page load
export const trackPoolView = (poolValue: number) => {
  gtag("event", "pool_view", {
    event_category: "metrics",
    value: poolValue,
    currency: "USD",
  });
};

// Track pool growth after a bet is placed
// This helps measure "Pool Growth" over time
export const trackPoolGrowth = (
  previousValue: number,
  newValue: number,
  betAmount: number
) => {
  const growth = newValue - previousValue;
  const growthPercent = previousValue > 0 ? (growth / previousValue) * 100 : 0;

  gtag("event", "pool_growth", {
    event_category: "metrics",
    previous_pool: previousValue,
    new_pool: newValue,
    growth_amount: growth,
    growth_percent: growthPercent,
    bet_amount: betAmount,
    currency: "USD",
  });
};

// Track network selection
export const trackNetworkSelect = (chainId: number, chainName: string) => {
  gtag("event", "network_select", {
    event_category: "engagement",
    chain_id: chainId,
    chain_name: chainName,
  });
};

// Track token selection
export const trackTokenSelect = (token: string, chainId: number) => {
  gtag("event", "token_select", {
    event_category: "engagement",
    token: token,
    chain_id: chainId,
  });
};

// Track country selection
export const trackCountrySelect = (countryCode: string) => {
  gtag("event", "country_select", {
    event_category: "engagement",
    country: countryCode,
  });
};

// Track claim attempt
export const trackClaimAttempt = () => {
  gtag("event", "claim_attempt", {
    event_category: "transaction",
  });
};

// Track successful claim
export const trackClaimSuccess = (amount: number, txHash: string) => {
  gtag("event", "claim_success", {
    event_category: "transaction",
    value: amount,
    currency: "USD",
    transaction_id: txHash,
  });
};

const analytics = {
  trackPageView,
  trackWalletConnect,
  trackWalletDisconnect,
  trackBetPlaced,
  trackBetSuccess,
  trackBetFailed,
  trackPoolView,
  trackPoolGrowth,
  trackNetworkSelect,
  trackTokenSelect,
  trackCountrySelect,
  trackClaimAttempt,
  trackClaimSuccess,
};

export default analytics;
